
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TaskService from "./TaskService";
import InternalUserDashboard, {
  filterProjectsByLookupApplications,
  parseInternalTaskTrackerPayload,
} from "./InternalUserDashboard";
import "./DashBoard.css";

const INTERNAL_VIEW_TYPES = {
  MY_TASKS: "my_tasks",
  MY_TEAM: "my_team",
};

function parseLookupApplicationsFromStorage() {
  try {
    const apps = JSON.parse(localStorage.getItem("application_details") || "[]");
    return Array.isArray(apps) ? apps : [];
  } catch {
    return [];
  }
}

/** Always refresh lookup for internal dashboard so application tiles match fetchLookupMasterData. */
async function loadLookupApplicationsForDashboard() {
  try {
    const lookup = await TaskService.fetchLookupMasterData(true);
    persistLookupMasterData(lookup);
    if (Array.isArray(lookup?.application_details)) {
      return lookup.application_details;
    }
  } catch (lookupErr) {
    console.warn("⚠️ Lookup master for internal dashboard failed:", lookupErr);
  }
  return parseLookupApplicationsFromStorage();
}

function persistLookupMasterData(lookup) {
  if (!lookup || typeof lookup !== "object") return;
  const entries = [
    ["application_details", "application_details"],
    ["task_status_details", "task_status_details"],
    ["task_type_details", "task_type_details"],
    ["task_priority_details", "task_priority_details"],
    ["release_type_details", "release_type_details"],
    ["employee_details", "employee_details"],
  ];
  for (const [jsonKey, storageKey] of entries) {
    const arr = lookup[jsonKey];
    if (Array.isArray(arr)) {
      localStorage.setItem(storageKey, JSON.stringify(arr));
      console.log(`✅ ${storageKey} stored (${arr.length} items)`);
    } else {
      console.warn(`⚠️ ${jsonKey} missing or not an array in lookup master response`);
    }
  }
}

function dashboardRoleStorageKey(email) {
  return `dashboard_selected_role_${String(email || "").trim().toLowerCase()}`;
}

/** Persist one chosen role row from fetchUserRole (single or multi-role pick). */
function persistUserRoleToStorage(userRoleData) {
  if (!userRoleData || typeof userRoleData !== "object") return;

  if (userRoleData.aa_app_rl_role_id !== undefined) {
    localStorage.setItem("user_role_id", String(userRoleData.aa_app_rl_role_id));
    console.log("✅ User role ID stored in localStorage:", userRoleData.aa_app_rl_role_id);
  }

  if (userRoleData.aa_app_employee_uid !== undefined) {
    localStorage.setItem("employee_uid", String(userRoleData.aa_app_employee_uid));
    console.log("✅ Employee UID stored:", userRoleData.aa_app_employee_uid);
  }

  if (userRoleData.aa_app_employee_fname && userRoleData.aa_app_employee_lname) {
    const fullName = `${userRoleData.aa_app_employee_fname} ${userRoleData.aa_app_employee_lname}`;
    localStorage.setItem("user_full_name", fullName);
    console.log("✅ Full name stored:", fullName);
  }

  if (userRoleData.aa_app_resource_deptname) {
    localStorage.setItem("user_department", userRoleData.aa_app_resource_deptname);
    console.log("✅ Department stored:", userRoleData.aa_app_resource_deptname);
  }

  if (userRoleData.aa_app_rl_isWrite !== undefined) {
    localStorage.setItem("is_write", String(userRoleData.aa_app_rl_isWrite));
    console.log("✅ Is Write stored:", userRoleData.aa_app_rl_isWrite);
  }

  if (userRoleData.aa_app_rl_role_name) {
    localStorage.setItem("user_role_name", userRoleData.aa_app_rl_role_name);
    console.log("✅ Role name stored:", userRoleData.aa_app_rl_role_name);
  }

  if (userRoleData.aa_app_rl_default_application_id !== undefined) {
    localStorage.setItem(
      "default_application_id",
      String(userRoleData.aa_app_rl_default_application_id)
    );
    console.log("✅ Default application ID stored:", userRoleData.aa_app_rl_default_application_id);
  }
}

function getDashboardRoleLabel() {
  const name = localStorage.getItem("user_role_name");
  if (name && String(name).trim()) return String(name).trim();
  const id = localStorage.getItem("user_role_id");
  if (id) return `Role ${id}`;
  return "";
}

/** Meta caption for standard dashboard (roles 2 / 3) — matches internal header pattern. */
function getStandardDashboardMetaLabel(activeRoleId) {
  const id = String(activeRoleId || localStorage.getItem("user_role_id") || "");
  if (id === "3") return "External";
  if (id === "2") return "Internal";
  return "Overview";
}

/** Role pill / dropdown in the meta row (engineering, production support, internal, external). */
function DashboardRoleMeta({
  availableUserRoles,
  activeRoleId,
  dashboardRoleLabel,
  roleSwitching,
  disabled,
  onRoleChange,
  metaLabel,
}) {
  const showRoleControl = availableUserRoles.length > 1 || !!dashboardRoleLabel;
  const showMetaLabel = Boolean(metaLabel) && showRoleControl;

  return (
    <div className="internal-dashboard-page-meta">
      {availableUserRoles.length > 1 ? (
        <div className="internal-dashboard-role-switch" aria-busy={roleSwitching}>
          <select
            className="internal-dashboard-page-role-select"
            aria-label="Switch active role"
            value={activeRoleId}
            onChange={onRoleChange}
            disabled={disabled}
          >
            {availableUserRoles.map((role) => (
              <option
                key={String(role.aa_app_rl_role_id)}
                value={String(role.aa_app_rl_role_id)}
              >
                {role.aa_app_rl_role_name?.trim() || `Role ${role.aa_app_rl_role_id}`}
              </option>
            ))}
          </select>
          {roleSwitching ? (
            <span
              className="dashboard-role-inline-spinner dashboard-role-inline-spinner--header"
              aria-hidden="true"
            />
          ) : null}
        </div>
      ) : dashboardRoleLabel ? (
        <span className="internal-dashboard-page-role">{dashboardRoleLabel}</span>
      ) : null}
      {showMetaLabel ? (
        <>
          <span className="internal-dashboard-page-meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="internal-dashboard-page-meta-label">{metaLabel}</span>
        </>
      ) : null}
    </div>
  );
}

function InternalDashboardActionButtons({ canCreate, onCreateTask, onViewAllTasks, onManageEmployees }) {
  const hideEmployees = localStorage.getItem("user_role_id") === "3";

  return (
    <>
      {canCreate ? (
        <button type="button" className="btn btn-primary" onClick={onCreateTask}>
          <i className="fas fa-plus-circle" aria-hidden="true" />
          Create New Task
        </button>
      ) : null}
      <button type="button" className="btn btn-success" onClick={onViewAllTasks}>
        <i className="fas fa-list" aria-hidden="true" />
        View All Tasks
      </button>
      {!hideEmployees ? (
        <button type="button" className="btn btn-secondary" onClick={onManageEmployees}>
          <i className="fas fa-users" aria-hidden="true" />
          View Employees
        </button>
      ) : null}
    </>
  );
}

/** Production support: All Tasks opens with an empty search box (no name prefill). */
function isProductionSupportRole() {
  const role = (localStorage.getItem("user_role_name") || "").toLowerCase();
  if (!role) return false;
  if (role.includes("production support")) return true;
  return role.includes("production") && role.includes("support");
}

/**
 * Engineering team only (not production support): prefill All Tasks search from profile / display name.
 * Role 4 / 5 still counts as engineering unless the role name is production support.
 */
function isEngineeringTeamRole() {
  if (isProductionSupportRole()) return false;
  const id = Number(localStorage.getItem("user_role_id"));
  const role = (localStorage.getItem("user_role_name") || "").toLowerCase();
  return id === 4 || id === 5 || role.includes("engineering");
}

function getEngineeringAllTasksSearchPrefill(fallbackDisplayName) {
  if (!isEngineeringTeamRole()) return "";
  const full = (localStorage.getItem("user_full_name") || "").trim();
  if (full) return full;
  return String(fallbackDisplayName || "").trim();
}

function getInternalMyTasksSearchPrefill(fallbackDisplayName) {
  const full = (localStorage.getItem("user_full_name") || "").trim();
  if (full) return full;
  return String(fallbackDisplayName || "").trim();
}

function getDefaultInternalDashboardViewType() {
  return isProductionSupportRole() ? INTERNAL_VIEW_TYPES.MY_TEAM : INTERNAL_VIEW_TYPES.MY_TASKS;
}

const Dashboard = ({ userData }) => {
  const navigate = useNavigate();

  // State for dashboard statistics
  const [stats, setStats] = useState({
    total_tasks: 0,
    completed_tasks: 0,
    in_progress_tasks: 0,
    cancelled_tasks: 0,
    not_started_tasks: 0,
    closed_tasks: 0,
  });

  /** Role 4 / 5: project tiles from fetchInternalUserDashboard */
  const [useInternalDashboard, setUseInternalDashboard] = useState(false);
  const [internalProjects, setInternalProjects] = useState([]);
  const [lookupApplications, setLookupApplications] = useState([]);
  const [internalDashboardViewType, setInternalDashboardViewType] = useState(
    getDefaultInternalDashboardViewType()
  );
  const [internalDashboardSwitching, setInternalDashboardSwitching] = useState(false);

  /** When fetchUserRole returns multiple roles and none saved yet — show picker before dashboard. */
  const [pendingMultiRole, setPendingMultiRole] = useState(null);

  /** All role rows when the user has more than one (for header / toolbar dropdown). */
  const [availableUserRoles, setAvailableUserRoles] = useState([]);
  /** In-dashboard role change: show overlay + spinner without full-page unmount. */
  const [roleSwitching, setRoleSwitching] = useState(false);
  const [roleSwitchError, setRoleSwitchError] = useState(null);
  /** Initial multi-role gate: pre-selected role in dropdown before Continue. */
  const [initialRolePickId, setInitialRolePickId] = useState("");

  // State for loading and error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!pendingMultiRole?.user_roles?.length) {
      setInitialRolePickId("");
      return;
    }
    const ids = pendingMultiRole.user_roles.map((r) => String(r.aa_app_rl_role_id));
    setInitialRolePickId((cur) => (cur && ids.includes(cur) ? cur : ids[0]));
  }, [pendingMultiRole]);

  const loadDashboardContent = useCallback(async () => {
    const userEmail = localStorage.getItem("userId");
    if (!userEmail) {
      setLoading(false);
      return;
    }

    const roleIdNum = Number(localStorage.getItem("user_role_id"));
    const employeeUid = localStorage.getItem("employee_uid");

    /** Internal roles: dedicated dashboard + fetchInternalUserDashboard (not manager counts). */
    if (roleIdNum === 4 || roleIdNum === 5) {
      const defaultInternalViewType = getDefaultInternalDashboardViewType();
      setInternalDashboardViewType(defaultInternalViewType);

      const apps = await loadLookupApplicationsForDashboard();

      const internalPayload = await TaskService.fetchInternalUserDashboard(
        userEmail,
        true,
        defaultInternalViewType
      );
      if (!isMountedRef.current) return;

      setLookupApplications(apps);
      const allProjects = parseInternalTaskTrackerPayload(internalPayload, {
        includeEnhancement: isEngineeringTeamRole(),
      });
      const projects = filterProjectsByLookupApplications(allProjects, apps);
      if (allProjects.length !== projects.length) {
        console.log(
          `📋 Internal dashboard: showing ${projects.length} of ${allProjects.length} projects (matched fetchLookupMasterData application_details)`
        );
      }
      setInternalProjects(projects);
      setUseInternalDashboard(true);
      setLoading(false);
      return;
    }

    if (!employeeUid) {
      throw new Error("Unable to fetch employee UID");
    }

    if (!isMountedRef.current) return;

    setUseInternalDashboard(false);

    const statsData = await TaskService.getDashboardStats(employeeUid, false);

    if (!isMountedRef.current) return;

    setStats({
      total_tasks: statsData.total_tasks || 0,
      completed_tasks: statsData.completed_tasks || 0,
      in_progress_tasks: statsData.in_progress_tasks || 0,
      cancelled_tasks: statsData.cancelled_tasks || 0,
      not_started_tasks: statsData.not_started_tasks || 0,
      closed_tasks: statsData.closed_tasks || 0,
    });

    setLoading(false);
  }, []);

  const handleMultiRoleSelected = async (userRoleData, options = {}) => {
    const fromDashboard = options.fromDashboard === true;
    const userEmail = localStorage.getItem("userId");
    if (!userEmail || !userRoleData) return;

    if (fromDashboard) {
      setRoleSwitchError(null);
      setRoleSwitching(true);
    } else {
      setError(null);
      setLoading(true);
    }

    if (pendingMultiRole?.user_roles?.length > 1) {
      setAvailableUserRoles(pendingMultiRole.user_roles);
    }

    try {
      localStorage.setItem(
        dashboardRoleStorageKey(userEmail),
        String(userRoleData.aa_app_rl_role_id)
      );
      persistUserRoleToStorage(userRoleData);
      setPendingMultiRole(null);
      await loadDashboardContent();
      if (fromDashboard) {
        setRoleSwitchError(null);
      }
    } catch (err) {
      console.error("Error loading dashboard after role selection:", err);
      if (fromDashboard) {
        setRoleSwitchError("Could not switch role. Please try again.");
      } else {
        setError("Failed to load dashboard data. Please try again later.");
      }
      if (!fromDashboard) {
        setLoading(false);
      }
    } finally {
      if (fromDashboard) {
        setRoleSwitching(false);
      }
    }
  };

  const handleDashboardRoleChange = async (event) => {
    const newId = String(event.target.value || "").trim();
    const prevId = String(localStorage.getItem("user_role_id") || "").trim();
    if (!newId || newId === prevId) return;
    const roleObj = availableUserRoles.find((r) => String(r.aa_app_rl_role_id) === newId);
    if (!roleObj) return;
    await handleMultiRoleSelected(roleObj, { fromDashboard: true });
  };

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
        try {
          setLoading(true);
          setPendingMultiRole(null);
          setRoleSwitchError(null);

          const userEmail = localStorage.getItem("userId");
        if (!userEmail) {
          throw new Error("User email not found in localStorage");
        }

        // Master lookups: fire in background — overlaps user role + stats; does not block the dashboard UI path
        void TaskService.fetchLookupMasterData(true)
          .then((lookup) => {
            if (!lookup || typeof lookup !== "object") {
              console.warn("⚠️ fetchLookupMasterData (background): empty response");
              return;
            }
            persistLookupMasterData(lookup);
            console.log("✅ Lookup master persisted (background). localStorage:", {
              application_details: !!localStorage.getItem("application_details"),
              task_status_details: !!localStorage.getItem("task_status_details"),
              task_type_details: !!localStorage.getItem("task_type_details"),
              task_priority_details: !!localStorage.getItem("task_priority_details"),
              release_type_details: !!localStorage.getItem("release_type_details"),
              employee_details: !!localStorage.getItem("employee_details"),
            });
          })
          .catch((err) => {
            console.warn("⚠️ fetchLookupMasterData (background) failed:", err);
          });

        try {
          console.log("Fetching user role for:", userEmail, "(lookup master already running in background)");
          const responseData = await TaskService.fetchUserRole(userEmail, true);

          if (!isMountedRef.current) return;

          console.log("User role response keys:", Object.keys(responseData));

          const userRoles = Array.isArray(responseData.user_roles) ? responseData.user_roles : [];
          if (userRoles.length === 0) {
            console.error("❌ User roles array is empty");
            throw new Error("User role data not found");
          }

          if (userRoles.length > 1) {
            setAvailableUserRoles(userRoles);
            const savedRaw = localStorage.getItem(dashboardRoleStorageKey(userEmail));
            const savedNum =
              savedRaw !== null && savedRaw !== "" && !Number.isNaN(Number(savedRaw))
                ? Number(savedRaw)
                : NaN;
            const matched = userRoles.find((r) => Number(r.aa_app_rl_role_id) === savedNum);

            if (matched) {
              persistUserRoleToStorage(matched);
              await loadDashboardContent();
              return;
            }

            setPendingMultiRole({
              user_roles: userRoles,
              roles: Array.isArray(responseData.roles) ? responseData.roles : [],
            });
            setLoading(false);
            return;
          }

          setAvailableUserRoles([]);
          persistUserRoleToStorage(userRoles[0]);
          await loadDashboardContent();
          return;
        } catch (roleError) {
          console.error("❌ Error fetching user role:", roleError);
          console.error("Error details:", roleError.message);
          try {
            const employeeUid = await TaskService.fetchUserIdByEmail(userEmail);
            if (employeeUid) {
              localStorage.setItem("employee_uid", String(employeeUid));
              console.log("✅ Fallback: Employee UID stored via fetchUserIdByEmail:", employeeUid);
            }
          } catch (fallbackError) {
            console.error("❌ Fallback method also failed:", fallbackError);
          }
          await loadDashboardContent();
          return;
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("Error fetching dashboard data:", err);
        setError("Failed to load dashboard data. Please try again later.");
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [loadDashboardContent]);

  const handleCreateTask = () => {
    navigate("/create-task");
  };

  const handleViewAllTasks = () => {
    const state = { from: "dashboard" };
    const prefill =
      useInternalDashboard && internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TASKS
        ? getInternalMyTasksSearchPrefill(userData?.name)
        : getEngineeringAllTasksSearchPrefill(userData?.name);
    if (prefill) state.presetSearchTerm = prefill;
    navigate("/all-tasks", { state });
  };

  const handleManageEmployees = () => {
    navigate("/employees");
  };

  const handleInternalDashboardViewChange = async (viewType) => {
    if (
      viewType === internalDashboardViewType ||
      internalDashboardSwitching ||
      roleSwitching
    ) {
      return;
    }

    const userEmail = localStorage.getItem("userId");
    if (!userEmail) return;

    try {
      setInternalDashboardSwitching(true);
      setInternalDashboardViewType(viewType);

      const apps = lookupApplications.length
        ? lookupApplications
        : await loadLookupApplicationsForDashboard();
      if (!lookupApplications.length && apps.length) {
        setLookupApplications(apps);
      }

      const internalPayload = await TaskService.fetchInternalUserDashboard(userEmail, true, viewType);
      const allProjects = parseInternalTaskTrackerPayload(internalPayload, {
        includeEnhancement: isEngineeringTeamRole(),
      });
      setInternalProjects(filterProjectsByLookupApplications(allProjects, apps));
      setError(null);
    } catch (err) {
      console.error("Error switching internal dashboard view:", err);
      setError("Failed to switch dashboard view. Please try again.");
    } finally {
      setInternalDashboardSwitching(false);
    }
  };

  const handleUpdateTask = (taskId) => {
    navigate(`/update-task/${taskId}`);
  };

  // Function to check if user can create tasks
  const canCreateTask = () => {
    const userRoleId = localStorage.getItem("user_role_id");
    const isWrite = localStorage.getItem("is_write");
    
    // Hide create task button if role_id is 3 and is_write is 0
    return !(userRoleId === "3" && isWrite === "0");
  };

  // Function to get the appropriate task title based on role
  const getTaskTitle = () => {
    const userRoleId = localStorage.getItem("user_role_id");
    return userRoleId === "3" ? "Tasks Owned by Me" : "Tasks Assigned to Me";
  };

  if (pendingMultiRole && Array.isArray(pendingMultiRole.user_roles) && pendingMultiRole.user_roles.length > 0) {
    return (
      <div className="dashboard dashboard--modern dashboard-role-picker-wrap">
        <div
          className="dashboard-role-picker-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-role-picker-title"
        >
          <h1 id="dashboard-role-picker-title" className="dashboard-title">
            Select your role
          </h1>
          <p className="dashboard-role-picker-hint">
            Your account has access to more than one role. Select a role below, then continue to open the
            matching dashboard. You can switch roles anytime from the dashboard.
          </p>
          <div className="dashboard-role-picker-field">
            <label htmlFor="dashboard-initial-role-select" className="dashboard-role-picker-select-label">
              Active role
            </label>
            <select
              id="dashboard-initial-role-select"
              className="dashboard-role-picker-select"
              value={initialRolePickId}
              onChange={(e) => setInitialRolePickId(e.target.value)}
              disabled={loading}
            >
              {pendingMultiRole.user_roles.map((role) => (
                <option key={String(role.aa_app_rl_role_id)} value={String(role.aa_app_rl_role_id)}>
                  {role.aa_app_rl_role_name?.trim() || `Role ${role.aa_app_rl_role_id}`}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary dashboard-role-picker-btn dashboard-role-picker-submit"
              disabled={!initialRolePickId || loading}
              onClick={() => {
                const r = pendingMultiRole.user_roles.find(
                  (x) => String(x.aa_app_rl_role_id) === initialRolePickId
                );
                if (r) handleMultiRoleSelected(r);
              }}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="dashboard dashboard--modern loading">
        <h1 className="dashboard-title">Dashboard</h1>
        <div className="dashboard-loading-content" aria-busy="true" aria-live="polite">
          <div className="dashboard-loading-text">Loading dashboard data…</div>
          <div className="dashboard-loading-spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="dashboard dashboard--modern error">
        <h1 className="dashboard-title">Dashboard</h1>
        <div className="error-message">{error}</div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          <i className="fas fa-sync" aria-hidden="true"></i>
          Retry
        </button>
      </div>
    );
  }

  if (useInternalDashboard) {
    const dashboardRoleLabel = getDashboardRoleLabel();
    const activeRoleId = String(localStorage.getItem("user_role_id") || "");
    const internalContentBusy = internalDashboardSwitching || roleSwitching;
    const internalSearchPrefillForSelectedView =
      internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TASKS
        ? getInternalMyTasksSearchPrefill(userData?.name)
        : "";

    return (
      <>
        <div className="dashboard dashboard--internal dashboard--modern">
          <header className="internal-dashboard-page-header">
            <div className="internal-dashboard-page-header-grid">
              <div className="internal-dashboard-page-header-left">
                <DashboardRoleMeta
                  availableUserRoles={availableUserRoles}
                  activeRoleId={activeRoleId}
                  dashboardRoleLabel={dashboardRoleLabel}
                  roleSwitching={roleSwitching}
                  disabled={internalContentBusy}
                  onRoleChange={handleDashboardRoleChange}
                  metaLabel="Workload by application"
                />
                {roleSwitchError ? (
                  <p className="dashboard-role-switch-error dashboard-role-switch-error--internal" role="alert">
                    {roleSwitchError}
                  </p>
                ) : null}
                <h1 className="dashboard-title dashboard-title--internal">Dashboard</h1>
                <div className="internal-dashboard-view-toggle" role="group" aria-label="Internal dashboard view">
                  <button
                    type="button"
                    className={`internal-dashboard-view-toggle-btn ${
                      internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TASKS ? "is-active" : ""
                    }`}
                    onClick={() => handleInternalDashboardViewChange(INTERNAL_VIEW_TYPES.MY_TASKS)}
                    disabled={internalContentBusy}
                    aria-pressed={internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TASKS}
                  >
                    My Tasks
                  </button>
                  <button
                    type="button"
                    className={`internal-dashboard-view-toggle-btn ${
                      internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TEAM ? "is-active" : ""
                    }`}
                    onClick={() => handleInternalDashboardViewChange(INTERNAL_VIEW_TYPES.MY_TEAM)}
                    disabled={internalContentBusy}
                    aria-pressed={internalDashboardViewType === INTERNAL_VIEW_TYPES.MY_TEAM}
                  >
                    My Team Tasks
                  </button>
                </div>
              </div>
              <div className="internal-dashboard-page-header-aside">
                <span className="internal-dashboard-page-badge">
                  {internalProjects.length} project{internalProjects.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="internal-dashboard-page-header-actions">
              <InternalDashboardActionButtons
                canCreate={canCreateTask()}
                onCreateTask={handleCreateTask}
                onViewAllTasks={handleViewAllTasks}
                onManageEmployees={handleManageEmployees}
              />
            </div>
            <p className="internal-dashboard-page-kicker">
              {isEngineeringTeamRole()
                ? "Bug, Enhancement, Query & Service Request — workload by application."
                : "Bug, Query & Service Request — workload by application."}{" "}
              Tap a tile for <strong>All Tasks</strong>. <strong>In progress</strong> and{" "}
              <strong>Not started</strong> are statuses. <strong>Overdue</strong> uses due-date logic from the
              tracker (details in the note below).
            </p>
          </header>
          <div className="internal-dashboard-content-shell">
            <InternalUserDashboard
              projects={internalProjects}
              applications={lookupApplications}
              searchPrefill={internalSearchPrefillForSelectedView}
              includeEnhancement={isEngineeringTeamRole()}
              viewType={internalDashboardViewType}
            />
            <footer className="internal-dashboard-actions">
              <InternalDashboardActionButtons
                canCreate={canCreateTask()}
                onCreateTask={handleCreateTask}
                onViewAllTasks={handleViewAllTasks}
                onManageEmployees={handleManageEmployees}
              />
            </footer>
            {internalContentBusy ? (
              <div
                className="internal-dashboard-content-overlay"
                role="status"
                aria-live="polite"
                aria-label={roleSwitching ? "Switching role" : "Loading dashboard content"}
              >
                <div className="internal-dashboard-content-overlay-spinner" aria-hidden="true" />
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  }

  const activeRoleIdStandard = String(localStorage.getItem("user_role_id") || "");
  const standardDashboardRoleLabel = getDashboardRoleLabel();

  return (
    <div className="dashboard dashboard--modern dashboard--standard">
      <header className="dashboard-standard-page-header">
        <div className="dashboard-standard-page-header-left">
          <DashboardRoleMeta
            availableUserRoles={availableUserRoles}
            activeRoleId={activeRoleIdStandard}
            dashboardRoleLabel={standardDashboardRoleLabel}
            roleSwitching={roleSwitching}
            disabled={roleSwitching}
            onRoleChange={handleDashboardRoleChange}
            metaLabel={getStandardDashboardMetaLabel(activeRoleIdStandard)}
          />
          {roleSwitchError ? (
            <p className="dashboard-role-switch-error dashboard-role-switch-error--internal" role="alert">
              {roleSwitchError}
            </p>
          ) : null}
          <h1 className="dashboard-title">Dashboard</h1>
        </div>
      </header>

      <div className="dashboard-body-shell">
      <div className="stats-container">
        <div className="stat-card total">
          <div className="stat-icon">
            <i className="fas fa-tasks"></i>
          </div>
          <div className="stat-details">
            <div className="stat-title">{getTaskTitle()}</div>
            <div className="stat-value">{stats.total_tasks}</div>
          </div>
        </div>

        <div className="stat-card not-started">
          <div className="stat-icon">
            <i className="fas fa-hourglass-start"></i>
          </div>
          <div className="stat-details">
            <div className="stat-title">Not started</div>
            <div className="stat-value">{stats.not_started_tasks}</div>
          </div>
        </div>

        <div className="stat-card in-progress">
          <div className="stat-icon">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-details">
            <div className="stat-title">In Progress</div>
            <div className="stat-value">{stats.in_progress_tasks}</div>
          </div>
        </div>

        <div className="stat-card completed">
          <div className="stat-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-details">
            <div className="stat-title">Completed</div>
            <div className="stat-value">{stats.completed_tasks}</div>
          </div>
        </div>

        {/* <div className="stat-card closed">
          <div className="stat-icon">
            <i className="fas fa-lock"></i>
          </div>
          <div className="stat-details">
            <div className="stat-title">Closed</div>
            <div className="stat-value">{stats.closed_tasks}</div>
          </div>
        </div> */}
      </div>

      <div className="action-buttons">
        {/* Conditionally render Create Task button */}
        {canCreateTask() && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreateTask}
          >
            <i className="fas fa-plus-circle" aria-hidden="true"></i>
            Create New Task
          </button>
        )}

        <button
          type="button"
          className="btn btn-success"
          onClick={handleViewAllTasks}
        >
          <i className="fas fa-list" aria-hidden="true"></i>
          View All Tasks
        </button>

        {/* <button
          className="action-button manage-employees"
          onClick={handleManageEmployees}
        >
          <i className="fas fa-users"></i>
          View Employees
        </button> */}

        {/* Conditionally render View Employees button - hide if role_id is 3 */}
        {localStorage.getItem("user_role_id") !== "3" && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleManageEmployees}
          >
            <i className="fas fa-users" aria-hidden="true"></i>
            View Employees
          </button>
        )}
      </div>
      {roleSwitching ? (
        <div
          className="internal-dashboard-content-overlay dashboard-role-switch-overlay"
          role="status"
          aria-live="polite"
          aria-label="Switching role"
        >
          <div className="internal-dashboard-content-overlay-spinner" aria-hidden="true" />
        </div>
      ) : null}
      </div>
    </div>
  );
};

export default Dashboard;